import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import SubLayout from '../../components/layout/SubLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import apiClient from '../../../core/http/axios';
import { useFrontAuthStore } from '../../auth/store/authStore';
import { getStudentProfile, updateStudentProfile, StudentProfile, UpdateStudentProfileRequest } from '../../services/profileService';
import { getUniversities, getDepartments, University, Department } from '../../services/commonService';
import SelectionModal from './components/SelectionModal';
import Modal from '../../components/ui/Modal';
import {
  validatePassword,
  validatePhone,
  validateStudentNumber,
  validateEmail,
  formatPhoneInput,
  formatStudentNumberInput,
  formatEmailInput,
} from '../../utils/formValidation';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['5xl']};
  width: 100%;
  max-width: 320px;
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.book.color1b};
`;

const SectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['5xl']};
`;

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['5xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.book.color1b};
  letter-spacing: -0.04em;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
  gap: ${props => props.theme.spacing.xl};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${props => props.theme.spacing.xs};
`;

const FieldLabel = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
  & > .required {
    color: #DF0707;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
  width: 100%;
  &.column {
    flex-flow: column nowrap;
    align-items: flex-start;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
`;

const ProfileSelect = styled.select<{ $hasError?: boolean }>`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.border.de};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.base};
  color: var(--text-primary);
  transition: all 0.2s;
  height: 44px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error[500] : props.theme.colors.book.color2b};
  }
`;

const ProfileInput = styled.input<{ $hasError?: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.border.de};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  transition: all 0.2s;
  height: 44px;
  width: 100%;
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error[500] : props.theme.colors.book.color2b};
  }
  &:disabled {
    background-color: ${props => props.theme.colors.gray[50]};
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid #DBDBDB;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: ${props => props.theme.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  width: 44px;

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
    border-color: ${props => props.theme.colors.gray[400]};
  }
`;

const VerifyTimer = styled.div`
  position: absolute;
  right: 110px;
  top: 12px;
  color: ${props => props.theme.colors.primary[600]};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VerifyButton = styled.button<{ $verified?: boolean }>`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$verified ? props.theme.colors.border.de : props.theme.colors.book.color6b};
  background-color: ${props => props.$verified ? props.theme.colors.border.de : 'var(--bg-secondary)'};
  color: ${props => props.$verified ? 'var(--bg-secondary)' : props.theme.colors.book.color2b};
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  font-weight: ${props => props.theme.fontWeight.semibold};

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
    
`;

const ValidationMessage = styled.div<{ $isError?: boolean }>`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.$isError ? props.theme.colors.error[500] : props.theme.colors.success[600]};
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};

  & > *:not(:only-child) {
    flex: 1 1 50%;
  }

  &:has(> :nth-child(2)) {
    justify-content: space-between;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: ${props => props.theme.colors.gray[500]};
`;

const ProfessorNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${props => props.theme.spacing['3xl']};
  text-align: center;
  color: ${props => props.theme.colors.gray[600]};
  font-size: ${props => props.theme.fontSize.base};
  line-height: 1.6;
`;

const ErrorMessage = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.error[500]};
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.green[50]};
  color: ${props => props.theme.colors.green[700]};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
`;

const MENU_ITEMS = [
  {
    key: 'profile',
    label: '프로필 수정',
    icon: '/images/book/icon_user.svg',
    active: true,
    children: [
      { key: 'settings-general', label: '기본 정보 설정' },
      { key: 'settings-school', label: '학적 정보 설정' },
    ],
  },
  { key: 'inquiry', label: '문의하기', icon: '/images/book/icon_inquiry.svg', path: '/inquiry' },
  { key: 'logout', label: '로그아웃' },
];

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useFrontAuthStore();
  const isProfessor = user?.role === 'professor';
  const [activeMenu, setActiveMenu] = useState('settings-general');

  const menuItems = useMemo(() => {
    if (isProfessor) {
      return [
        {
          key: 'profile',
          label: '프로필 수정',
          icon: '/images/book/icon_user.svg',
          active: true,
          children: [
            { key: 'settings-general', label: '기본 정보 설정' },
          ],
        },
        { key: 'inquiry', label: '문의하기', icon: '/images/book/icon_inquiry.svg', path: '/inquiry' },
        { key: 'logout', label: '로그아웃' },
      ];
    }
    return MENU_ITEMS;
  }, [isProfessor]);
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    tel: '',
    emailId: '',
    emailDomain: '',
    student_number: '',
    university_no: 0,
    department_no: 0,
    password: '',
    password_confirm: '',
  });

  const [emailVerification, setEmailVerification] = useState({
    requestUuid: '',
    verificationCode: '',
    isRequested: false,
    isVerified: false,
    isRequesting: false,
    isVerifying: false,
    error: '',
    countdown: 0,
  });
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [universities, setUniversities] = useState<University[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

  const [isUniversityModalOpen, setIsUniversityModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  const [passwordValidation, setPasswordValidation] = useState<{ isValid: boolean; message: string }>({ isValid: false, message: '' });
  const [telValidation, setTelValidation] = useState<{ isValid: boolean; message: string }>({ isValid: false, message: '' });
  const [studentNumberValidation, setStudentNumberValidation] = useState<{ isValid: boolean; message: string }>({ isValid: false, message: '' });
  const [emailValidation, setEmailValidation] = useState<{ isValid: boolean; message: string }>({ isValid: false, message: '' });
  const [emailDomainType, setEmailDomainType] = useState<'select' | 'input'>('select');
  const [selectedDomainOption, setSelectedDomainOption] = useState<string>('');
  const emailDomains = ['gmail.com', 'naver.com', 'daum.net', 'hanmail.net', 'kakao.com', '직접입력'];

  const handleEmailDomainChange = (value: string) => {
    setSelectedDomainOption(value);
    if (value === '직접입력') {
      setEmailDomainType('input');
      setFormData(prev => ({ ...prev, emailDomain: '' }));
    } else {
      setEmailDomainType('select');
      handleEmailPartChange('emailDomain', value);
    }
  };

  const [fieldTouched, setFieldTouched] = useState({
    tel: false,
    student_number: false,
    email: false,
  });

  useEffect(() => {
    const initialize = async () => {
      await loadUniversities();
      await loadProfile();
    };
    initialize();
  }, []);

  useEffect(() => {
    // 프로필이 로드되고 학교 목록이 있을 때 선택된 학교 설정
    if (profile?.university_no && universities.length > 0 && !selectedUniversity) {
      const univ = universities.find(u => u.university_no === profile.university_no);
      if (univ) {
        setSelectedUniversity(univ);
      }
    }
  }, [profile, universities, selectedUniversity]);

  useEffect(() => {
    // 프로필이 로드되고 학과 목록이 있을 때 선택된 학과 설정
    if (profile?.department_no && departments.length > 0 && !selectedDepartment) {
      const dept = departments.find(d => d.department_no === profile.department_no);
      if (dept) {
        setSelectedDepartment(dept);
      }
    }
  }, [profile, departments, selectedDepartment]);

  useEffect(() => {
    if (formData.university_no > 0) {
      loadDepartments(formData.university_no);
    } else {
      setDepartments([]);
      setSelectedDepartment(null);
      setFormData(prev => ({ ...prev, department_no: 0 }));
    }
  }, [formData.university_no]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await getStudentProfile();
      setProfile(data);
      const rawTel = data.tel || '';
      const tel = rawTel.includes('-') ? rawTel : formatPhoneInput(rawTel);
      const studentNumber = data.student_number || '';
      const emailParts = (data.email || '').split('@');
      const emailId = emailParts[0] || '';
      const emailDomain = emailParts[1] || '';
      setFormData({
        id: data.id ?? (data as any).login_id ?? '',
        name: data.name || '',
        tel,
        emailId,
        emailDomain,
        student_number: studentNumber,
        university_no: data.university_no || 0,
        department_no: data.department_no || 0,
        password: '',
        password_confirm: '',
      });
      setTelValidation(tel ? validatePhone(tel) : { isValid: false, message: '' });
      setStudentNumberValidation(studentNumber ? validateStudentNumber(studentNumber) : { isValid: false, message: '' });
      setEmailValidation(data.email ? validateEmail(data.email) : { isValid: false, message: '' });
      if (emailDomain && emailDomains.includes(emailDomain)) {
        setEmailDomainType('select');
        setSelectedDomainOption(emailDomain);
      } else if (emailDomain) {
        setEmailDomainType('input');
        setSelectedDomainOption('직접입력');
      }
      setFieldTouched({ tel: false, student_number: false, email: false });
      setEmailVerification(prev => ({
        ...prev,
        isVerified: !!data.email,
        error: '',
      }));
    } catch (err: any) {
      setError('프로필을 불러오는데 실패했습니다.');
      // console.error('프로필 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUniversities = async (): Promise<University[]> => {
    try {
      const data = await getUniversities();
      setUniversities(data);
      return data;
    } catch (err: any) {
      // console.error('학교 목록 로드 실패:', err);
      return [];
    }
  };

  const loadDepartments = async (universityNo: number) => {
    try {
      setIsLoadingDepartments(true);
      const data = await getDepartments(universityNo);
      setDepartments(data);
      if (formData.department_no) {
        const dept = data.find(d => d.department_no === formData.department_no);
        if (dept) {
          setSelectedDepartment(dept);
        }
      }
    } catch (err: any) {
      // console.error('학과 목록 로드 실패:', err);
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  const handleUniversitySelect = (item: University | Department) => {
    if ('university_no' in item) {
      setSelectedUniversity(item);
      setFormData(prev => ({
        ...prev,
        university_no: item.university_no,
        department_no: 0,
      }));
      setSelectedDepartment(null);
    }
  };

  const handleDepartmentSelect = (item: University | Department) => {
    if ('department_no' in item) {
      setSelectedDepartment(item);
      setFormData(prev => ({
        ...prev,
        department_no: item.department_no,
      }));
    }
  };

  const handleEmailVerificationRequest = async () => {
    const email = formData.emailId && formData.emailDomain ? `${formData.emailId}@${formData.emailDomain}`.trim() : '';
    if (!email) {
      setEmailVerification(prev => ({ ...prev, error: '이메일을 입력해주세요.' }));
      return;
    }
    const emailValidationResult = validateEmail(email);
    if (!emailValidationResult.isValid) {
      setEmailVerification(prev => ({ ...prev, error: emailValidationResult.message }));
      return;
    }

    setEmailVerification(prev => ({ ...prev, isRequesting: true, error: '' }));

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: { request_uuid: string };
      }>('/auth/student/email/request', { email });

      if (response.data.success && response.data.data?.request_uuid) {
        setEmailVerification(prev => ({
          ...prev,
          requestUuid: response.data.data.request_uuid,
          isRequested: true,
          isRequesting: false,
          error: '',
          countdown: 180,
        }));
      } else {
        const apiError = response.data.error;
        const errorMessage = apiError === 'EMAIL_ALREADY_USED'
          ? '이미 사용중인 이메일입니다.'
          : (apiError || '인증코드 발송에 실패했습니다.');
        setEmailVerification(prev => ({
          ...prev,
          isRequesting: false,
          error: errorMessage,
        }));
      }
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      const errorMessage = apiError === 'EMAIL_ALREADY_USED'
        ? '이미 사용중인 이메일입니다.'
        : (apiError || '인증 요청 중 오류가 발생했습니다.');
      setEmailVerification(prev => ({
        ...prev,
        isRequesting: false,
        error: errorMessage,
      }));
    }
  };

  const handleEmailVerificationVerify = async () => {
    if (!emailVerification.requestUuid || !emailVerification.verificationCode) {
      setEmailVerification(prev => ({ ...prev, error: '인증 코드를 입력해주세요.' }));
      return;
    }

    setEmailVerification(prev => ({ ...prev, isVerifying: true, error: '' }));

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: { verified: boolean };
      }>('/auth/student/email/verify', {
        request_uuid: emailVerification.requestUuid,
        code: emailVerification.verificationCode,
      });

      if (response.data.success && response.data.data?.verified) {
        setEmailVerification(prev => ({
          ...prev,
          isVerified: true,
          isVerifying: false,
          error: '',
        }));
      } else {
        setEmailVerification(prev => ({
          ...prev,
          isVerifying: false,
          error: response.data.error || '인증에 실패했습니다.',
        }));
      }
    } catch (err: any) {
      setEmailVerification(prev => ({
        ...prev,
        isVerifying: false,
        error: err.response?.data?.error || '인증 확인 중 오류가 발생했습니다.',
      }));
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (emailVerification.isRequested && emailVerification.countdown > 0 && !emailVerification.isVerified) {
      countdownTimerRef.current = setInterval(() => {
        setEmailVerification(prev => {
          if (prev.countdown <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            return { ...prev, countdown: 0 };
          }
          return { ...prev, countdown: prev.countdown - 1 };
        });
      }, 1000);
    }
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [emailVerification.isRequested, emailVerification.countdown, emailVerification.isVerified]);

  const handlePasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password: value }));
    setPasswordValidation(value ? validatePassword(value) : { isValid: false, message: '' });
  };

  const handleTelChange = (value: string) => {
    setFieldTouched(prev => ({ ...prev, tel: true }));
    const formatted = formatPhoneInput(value);
    setFormData(prev => ({ ...prev, tel: formatted }));
    setTelValidation(formatted ? validatePhone(formatted) : { isValid: false, message: '' });
  };

  const handleStudentNumberChange = (value: string) => {
    setFieldTouched(prev => ({ ...prev, student_number: true }));
    const formatted = formatStudentNumberInput(value);
    setFormData(prev => ({ ...prev, student_number: formatted }));
    setStudentNumberValidation(formatted ? validateStudentNumber(formatted) : { isValid: false, message: '' });
  };

  const resetEmailVerification = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setEmailVerification({
      requestUuid: '',
      verificationCode: '',
      isRequested: false,
      isVerified: false,
      isRequesting: false,
      isVerifying: false,
      error: '',
      countdown: 0,
    });
  };

  const handleEmailPartChange = (field: 'emailId' | 'emailDomain', value: string) => {
    const formatted = formatEmailInput(value);
    setFieldTouched(prev => ({ ...prev, email: true }));
    setFormData(prev => {
      const updated = { ...prev, [field]: formatted };
      const fullEmail = updated.emailId && updated.emailDomain ? `${updated.emailId}@${updated.emailDomain}` : '';
      setEmailValidation(fullEmail ? validateEmail(fullEmail) : { isValid: false, message: '' });
      return updated;
    });
    if (emailVerification.isRequested || emailVerification.isVerified) {
      resetEmailVerification();
    } else {
      setEmailVerification(prev => ({ ...prev, error: '' }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password_confirm: value }));
  };

  const getPasswordConfirmError = () => {
    if (!formData.password_confirm) return null;
    if (formData.password !== formData.password_confirm) return '비밀번호가 일치하지 않습니다.';
    if (formData.password && passwordValidation.isValid) return null;
    return null;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (formData.password) {
        if (!passwordValidation.isValid) {
          setError('영문, 숫자, 특수문자 포함 8자 이상의 비밀번호를 입력해주세요.');
          return;
        }
        if (formData.password !== formData.password_confirm) {
          setError('비밀번호가 일치하지 않습니다.');
          return;
        }
      }

      if (formData.tel && !telValidation.isValid) {
        setError(telValidation.message);
        return;
      }
      if (formData.student_number && !studentNumberValidation.isValid) {
        setError(studentNumberValidation.message);
        return;
      }
      const fullEmail = formData.emailId && formData.emailDomain ? `${formData.emailId}@${formData.emailDomain}` : '';
      if (fullEmail && !emailValidation.isValid) {
        setError(emailValidation.message);
        return;
      }
      if (fullEmail && !emailVerification.isVerified) {
        setError('이메일 인증을 완료해주세요.');
        return;
      }

      if (emailVerification.error) {
        setError(emailVerification.error);
        return;
      }

      const updateData: UpdateStudentProfileRequest = {
        name: formData.name,
        tel: formData.tel.replace(/-/g, ''),
        university_no: formData.university_no,
        department_no: formData.department_no,
        student_number: formData.student_number,
        email: fullEmail && emailVerification.isVerified ? fullEmail : undefined,
      };

      if (formData.password) {
        updateData.password = formData.password;
        updateData.password_confirm = formData.password_confirm;
      }

      const updatedProfile = await updateStudentProfile(updateData);
      setProfile(updatedProfile);
      setSuccess('프로필이 성공적으로 저장되었습니다.');
      setFormData(prev => ({
        ...prev,
        password: '',
        password_confirm: '',
      }));
      setPasswordValidation({ isValid: false, message: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || '프로필 저장에 실패했습니다.');
      // console.error('프로필 저장 실패:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMenuClick = useCallback(async (key: string) => {
    if (key === 'logout') {
      try {
        await logout();
      } catch (e) { /* ignore */ }
      return;
    }
    setActiveMenu(key);
  }, [logout]);

  if (loading) {
    return (
      <SubLayout
        menuItems={menuItems}
        activeMenu={activeMenu}
        onMenuClick={handleMenuClick}
        contentAlign="center"
      >
        <LoadingContainer>로딩 중...</LoadingContainer>
      </SubLayout>
    );
  }

  return (
    <SubLayout
      menuItems={menuItems}
      activeMenu={activeMenu}
      onMenuClick={handleMenuClick}
      contentAlign="left"
    >
      <Container>
        <PageTitle>프로필 수정</PageTitle>

        {isProfessor ? (
          <ProfessorNotice>
            교수의 프로필 수정은 관리자에게 문의해주세요.
          </ProfessorNotice>
        ) : (
          <>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <SectionsGrid>
          {/* 기본정보 수정 섹션 */}
          {activeMenu === 'settings-general' && <SectionCard>
            <SectionTitle>기본정보 수정</SectionTitle>
            <FormFields>
              <FormField>
                <FieldLabel>ID</FieldLabel>
                <Input
                  value={formData.id}
                  onChange={() => {}}
                  placeholder="ID"
                  disabled
                />
              </FormField>
              <FormField>
                <FieldLabel>이름</FieldLabel>
                <Input
                  value={formData.name}
                  onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                  placeholder="김이박 이름"
                />
              </FormField>
              <FormField>
                <FieldLabel>휴대번호</FieldLabel>
                <Input
                  value={formData.tel}
                  onChange={(value) => handleTelChange(value)}
                  placeholder="010-1234-1234"
                  error={fieldTouched.tel && formData.tel && !telValidation.isValid ? telValidation.message : undefined}
                />
                {fieldTouched.tel && formData.tel && telValidation.isValid && (
                  <ValidationMessage $isError={false}>{telValidation.message}</ValidationMessage>
                )}
              </FormField>
              <FormField>
                <FieldLabel>이메일</FieldLabel>
                <InputGroup>
                  <ProfileInput
                    type="text"
                    placeholder="이메일 아이디"
                    value={formData.emailId}
                    style={{ width: '48%' }}
                    onChange={(e) => handleEmailPartChange('emailId', e.target.value)}
                    $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
                  />
                  <span style={{ alignSelf: 'center' }}>@</span>
                  {emailDomainType === 'select' ? (
                    <ProfileSelect
                      style={{ width: '48%' }}
                      value={selectedDomainOption || formData.emailDomain || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleEmailDomainChange(e.target.value)}
                      $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
                    >
                      <option value="">도메인 선택</option>
                      {emailDomains.map((domain) => (
                        <option key={domain} value={domain}>{domain}</option>
                      ))}
                    </ProfileSelect>
                  ) : (
                    <ProfileInput
                      type="text"
                      placeholder="도메인 직접입력"
                      style={{ width: '48%' }}
                      value={formData.emailDomain}
                      onChange={(e) => handleEmailPartChange('emailDomain', e.target.value)}
                      $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
                    />
                  )}
                </InputGroup>
                {!emailVerification.isRequested && !emailVerification.isVerified && (
                  <VerifyButton
                    type="button"
                    onClick={handleEmailVerificationRequest}
                    disabled={!formData.emailId || !formData.emailDomain || emailVerification.isRequesting}
                    $verified={false}
                  >
                    {emailVerification.isRequesting ? '요청중...' : '이메일 인증하기'}
                  </VerifyButton>
                )}
                {emailVerification.isRequested && !emailVerification.isVerified && (
                  <>
                    
                    <InputGroup style={{ marginTop: '0.5rem', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {emailVerification.countdown > 0 && (
                        <VerifyTimer>{formatTime(emailVerification.countdown)}</VerifyTimer>
                      )}
                      <ProfileInput
                        type="text"
                        placeholder="인증 코드 입력"
                        value={emailVerification.verificationCode}
                        onChange={(e) => setEmailVerification(prev => ({ ...prev, verificationCode: e.target.value, error: '' }))}
                        $hasError={!!emailVerification.error}
                        maxLength={6}
                      />
                      <VerifyButton
                        type="button"
                        onClick={handleEmailVerificationVerify}
                        disabled={!emailVerification.verificationCode || emailVerification.isVerifying}
                        $verified={false}
                        style={{ marginTop: 0 }}
                      >
                        {emailVerification.isVerifying ? '확인중...' : '인증확인'}
                      </VerifyButton>
                    </InputGroup>
                  </>
                )}
                {emailVerification.isVerified && (
                  <VerifyButton type="button" disabled $verified>인증완료</VerifyButton>
                )}
                {emailVerification.error && (
                  <ValidationMessage $isError>{emailVerification.error}</ValidationMessage>
                )}
                {emailVerification.isVerified && (
                  <ValidationMessage $isError={false}>이메일 인증이 완료되었습니다.</ValidationMessage>
                )}
              </FormField>
              <FormField>
                <FieldLabel>비밀번호</FieldLabel>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(value) => handlePasswordChange(value)}
                  placeholder="영문, 숫자, 특수문자 포함 8자 이상"
                  error={formData.password && !passwordValidation.isValid ? passwordValidation.message : undefined}
                />
                {formData.password && passwordValidation.isValid && (
                  <ValidationMessage $isError={false}>{passwordValidation.message}</ValidationMessage>
                )}
                <Input
                  type="password"
                  value={formData.password_confirm}
                  onChange={(value) => handleConfirmPasswordChange(value)}
                  placeholder="비밀번호 재입력"
                  error={getPasswordConfirmError() ?? undefined}
                />
              </FormField>
            </FormFields>
          </SectionCard>}

          {/* 학적정보 수정 섹션 */}
          {activeMenu === 'settings-school' && <SectionCard>
            <SectionTitle>학적정보 수정</SectionTitle>
            <FormFields>
              <FormField>
                <FieldLabel>대학교</FieldLabel>
                <InputGroup>
                  <InputWrapper>
                    <Input
                      value={selectedUniversity?.name || ''}
                      onChange={() => {}}
                      placeholder="00 대학교"
                      disabled
                    />
                  </InputWrapper>
                  {/* <ButtonWrapper>
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => setIsUniversityModalOpen(true)}
                    >
                      검색
                    </Button>
                  </ButtonWrapper> */}
                </InputGroup>
              </FormField>
              <FormField>
                <FieldLabel>학과</FieldLabel>
                <InputGroup>
                  <InputWrapper>
                    <Input
                      value={selectedDepartment?.name || ''}
                      onChange={() => {}}
                      placeholder="00 학과"
                      disabled
                    />
                  </InputWrapper>
                  {/* <ButtonWrapper>
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => setIsDepartmentModalOpen(true)}
                      disabled={!formData.university_no}
                    >
                      검색
                    </Button>
                  </ButtonWrapper> */}
                </InputGroup>
              </FormField>
              <FormField>
                <FieldLabel>학번</FieldLabel>
                <Input
                  value={formData.student_number}
                  onChange={() => {}}
                  placeholder="2029340234"
                  disabled
                  error={fieldTouched.student_number && formData.student_number && !studentNumberValidation.isValid ? studentNumberValidation.message : undefined}
                />
                {fieldTouched.student_number && formData.student_number && studentNumberValidation.isValid && (
                  <ValidationMessage $isError={false}>{studentNumberValidation.message}</ValidationMessage>
                )}
              </FormField>
            </FormFields>
          </SectionCard>}
        </SectionsGrid>

        {/* 저장 버튼 */}
        <SaveButtonContainer>
          <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate(-1)}
          >
            뒤로가기
          </Button>
          <Button
            variant="primary"
            size="lg"
            fullWidth={true}
            onClick={handleSave}
            loading={saving}
            disabled={
              saving ||
              !!emailVerification.error ||
              (!!formData.tel && !telValidation.isValid) ||
              (!!formData.student_number && !studentNumberValidation.isValid) ||
              (!!(formData.emailId && formData.emailDomain) && !emailValidation.isValid) ||
              (!!(formData.emailId && formData.emailDomain) && !emailVerification.isVerified) ||
              (!!formData.password && !passwordValidation.isValid) ||
              (!!formData.password && !!formData.password_confirm && formData.password !== formData.password_confirm)
            }
            className="save-button"
          >
            저장
          </Button>
        </SaveButtonContainer>

        {/* 학교 선택 모달 */}
        <SelectionModal
          isOpen={isUniversityModalOpen}
          onClose={() => setIsUniversityModalOpen(false)}
          title="대학교 선택"
          items={universities}
          selectedItem={selectedUniversity}
          onSelect={handleUniversitySelect}
        />

        {/* 학과 선택 모달 */}
        <SelectionModal
          isOpen={isDepartmentModalOpen}
          onClose={() => setIsDepartmentModalOpen(false)}
          title="학과 선택"
          items={departments}
          selectedItem={selectedDepartment}
          onSelect={handleDepartmentSelect}
          isLoading={isLoadingDepartments}
        />

        {/* 저장 완료 모달 */}
        <Modal
          isOpen={!!success}
          onClose={() => setSuccess(null)}
          title="수정 완료"
          showCloseButton
        >
          <SuccessMessage>{success}</SuccessMessage>
        </Modal>
          </>
        )}
      </Container>
    </SubLayout>
  );
};

export default UserProfile;
